<template>
    <IntegrationModal
        title="Brevo"
        documentation-url="https://support.topol.io/en/articles/6386382-export-to-sendinblue"
        :invalid-validation="v$.$invalid"
        :loading="loading"
        :edit-mode="integration !== undefined"
        @close="$emit('close')"
        @create="createIntegration"
        @edit="updateIntegration"
    >
        <template #inputs>
            <div class="mb-6 w-full">
                <TextInput
                    v-model="v$.name.$model"
                    class="mt-6"
                    placeholder="Enter your preferred integration name"
                    label="Name"
                    :error="getError('name')"
                />

                <TextInput
                    v-model="v$.api_key.$model"
                    class="mt-6 w-full"
                    placeholder="Your Brevo API key"
                    label="API key"
                    required
                    :error="getError('api_key')"
                />

                <TextInput
                    v-model="v$.sender_name.$model"
                    class="mt-6 w-full"
                    placeholder="Your Brevo sender name"
                    label="Sender name"
                    required
                    :error="getError('sender_name')"
                />

                <TextInput
                    v-model="v$.sender_email.$model"
                    class="mt-6 w-full"
                    placeholder="Your Brevo sender email"
                    label="Sender email"
                    required
                    :error="getError('sender_email')"
                />
            </div>
        </template>
    </IntegrationModal>
</template>
<script lang="ts" setup>
import { route } from 'ziggy-js';
import { create } from '@composables/useNotifications';
import useVuelidate from '@vuelidate/core';
import { maxLength, required } from '@vuelidate/validators';
import IInputErrors from '@/types/Atomic/IInputErrors';
import { PropType } from 'vue';
import { IBrevo, IIntegration } from '@/types/Integrations/IIntegration';
import useIntegrations from '../../composables/useIntegrations';

const props = defineProps({
    integration: {
        type: Object as PropType<IIntegration<IBrevo>>,
        required: false,
        default: undefined,
    },
});
const emit = defineEmits(['close']);

const loading = ref(false);
const errors = ref<IInputErrors>({});

const { brevoIntegrationModalOpened, integrationCreated } = useIntegrations();
const state = reactive({
    name: props.integration?.name || '',
    api_key: '',
    sender_name: props.integration?.data.sender.name || '',
    sender_email: props.integration?.data.sender.email || '',
});

const rules = {
    name: {
        maxLength: maxLength(255),
    },
    api_key: {
        required,
        maxLength: maxLength(255),
    },
    sender_name: {
        required,
        maxLength: maxLength(255),
    },
    sender_email: {
        required,
        maxLength: maxLength(255),
    },
};

const v$ = useVuelidate(rules, state);

const getError = (field: string) => {
    return v$.value[field]?.$errors[0]?.$message || errors.value?.[field] || '';
};

const createIntegration = () => {
    v$.value.$touch();
    if (v$.value.$error) {
        return;
    } else {
        loading.value = true;
        router.post(
            route('integrations.brevo.store'),
            {
                name: state.name,
                api_key: state.api_key,
                sender_name: state.sender_name,
                sender_email: state.sender_email,
            },
            {
                preserveScroll: true,
                onSuccess: () => {
                    create({
                        title: 'Integration added',
                        text: 'Brevo integration has been successfully added.',
                        type: 'success',
                    });
                    integrationCreated('Brevo');
                    brevoIntegrationModalOpened.value = false;
                },
                onError: (err) => {
                    errors.value = err;
                    create({
                        title: 'Integration not added',
                        text:
                            err.message ??
                            'There was an error adding Brevo integration.',
                        type: 'error',
                    });
                },
                onFinish: () => {
                    loading.value = false;
                },
            }
        );
    }
};

const updateIntegration = () => {
    v$.value.$touch();
    if (v$.value.$error || !props.integration) {
        return;
    } else {
        loading.value = true;
        router.put(
            route('integrations.brevo.update', {
                integration: props.integration.id,
            }),
            {
                name: state.name,
                api_key: state.api_key,
                sender_name: state.sender_name,
                sender_email: state.sender_email,
            },
            {
                preserveScroll: true,
                onSuccess: () => {
                    emit('close');
                    create({
                        title: 'Integration updated',
                        type: 'success',
                    });
                },
                onError: (err) => {
                    errors.value = err;
                    create({
                        title: 'Integration not updated',
                        text:
                            err.message ??
                            'There was an error updating Brevo integration.',
                        type: 'error',
                    });
                },
                onFinish: () => {
                    loading.value = false;
                },
            }
        );
    }
};
</script>

<style scoped></style>
